import type { UseNavigateResult } from '@tanstack/react-router';
import type { AxiosError } from 'axios';
import { useEffect } from 'react';

type UseApiErrorHandlerOptions = {
  /** エラーオブジェクト（TanStack Query の error） */
  error: Error | null;
  /** TanStack Router の navigate 関数 */
  navigate: UseNavigateResult<string>;
};

/**
 * APIエラーハンドリングの共通ロジック
 *
 * **使用例**:
 * ```tsx
 * const { data, isLoading, error } = useGetProjects(filters);
 * const navigate = useNavigate();
 *
 * useApiErrorHandler({
 *   error,
 *   navigate,
 * });
 * ```
 *
 * **エラーハンドリングルール**:
 * - 401 Unauthorized: リダイレクトしない（インターセプターでログインページへリダイレクト済み）
 * - 403 Forbidden: `/403` ページへリダイレクト（インターセプターでトースト通知済み）
 * - 404 Not Found: `/404` ページへリダイレクト（インターセプターでトースト通知済み）
 * - 500 Internal Server Error: `/500` ページへリダイレクト（インターセプターでトースト通知済み）
 * - ネットワークエラー（responseなし）: リダイレクトしない（インターセプターでトースト通知済み）
 * - その他のエラー: `/404` ページへリダイレクト（不正なIDなどでユーザーが混乱しないようにする）
 *
 * **注意**: トースト通知は `client.ts` のインターセプターで既に表示されているため、
 * このフックではリダイレクトなどの追加処理のみを行います。
 */
export const useApiErrorHandler = ({ error, navigate }: UseApiErrorHandlerOptions) => {
  useEffect(() => {
    if (error) {
      // AxiosErrorかどうかを判定
      const axiosError = error as unknown as AxiosError;
      const status = axiosError.response?.status;

      // AxiosErrorでない場合は処理しない
      if (!axiosError.isAxiosError) {
        return;
      }

      // ネットワークエラー（responseなし）: リダイレクトせず、インターセプターのトースト通知のみで対応
      if (!axiosError.response) {
        return;
      }

      // 401 Unauthorized: インターセプターで既にログインページへリダイレクト済み
      if (status === 401) {
        return;
      }

      // 402 Payment Required: インターセプターでトークン制限トースト表示済み
      if (status === 402) {
        return;
      }

      if (status === 403) {
        navigate({ to: '/403' });
        return;
      }

      if (status === 500) {
        navigate({ to: '/500' });
        return;
      }

      // その他のステータスコード（404含む）: 不正なIDなどでユーザーが混乱しないよう /404 へリダイレクト
      navigate({ to: '/404' });
    }
  }, [error, navigate]);
};
